import uuidv4 from 'uuid/v4';
import { ParseError } from '@arcweave/arcscript';
import basic from './common/basic.js';
import rect from './common/rect.js';
import updater from '../updater.js';
import {
  roundToNearestMultiple,
  addPrefixToObjectKeys,
  removeEnclosingParagraph,
} from '../../../utils.js';

/* State example:
state: {
  byId: {
    "note_1": {
      "x": 10205,
      "y": 10469,
      "id": "note_1",
      "theme": "cyan",
      "width": 138,
      "height": 50,
      "content": "",
    },
    "note_2": {
      "x": 10425,
      "y": 10293,
      "id": "note_2",
      "theme": "purple",
      "width": 410,
      "height": 387,
      "content": "<p><i>Day, outside a village inn, west England 1850 A.D.</i></p><span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;is doing his daily chores at the <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_10\" data-type=\"component\">Admiral Benbow Inn</span>&nbsp;when&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;arrives and demands infomation about a patron,&nbsp;<span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_11\" data-type=\"component\">Billy Bones</span>.<p>When <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_2\" data-type=\"component\">Jim</span>&nbsp;tries to escape, <span class=\"mention mention-component\" contenteditable=\"false\" data-id=\"comp_1\" data-type=\"component\">Black Dog</span>&nbsp;strikes him with the hilt of his cutlass and he loses consciousness.</p><p><i>Fade to Black.</i></p><p></p>",
    },
  }
}
*/
const NEW_BRANCH = {
  conditions: { ifCondition: null },
  theme: 'default',
};
const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...rect.getters(options),

  /**
   * Return all the condition ids of the given branch
   * @param   {String}  id    The branch id
   * @returns {Array}         The condition ids for the branch
   */
  getConditions(state) {
    return id => {
      const { ifCondition, elseIfConditions, elseCondition } = state.byId[
        id
      ].conditions;
      const result = [];
      if (ifCondition) {
        result.push(ifCondition);
      }
      if (elseIfConditions) {
        result.push(...elseIfConditions);
      }
      if (elseCondition) {
        result.push(elseCondition);
      }
      return result;
    };
  },

  /**
   * Return the branch that contains a condition
   * @param {string} id The condition id
   * @returns {object} The branch where the condition is found or null
   */
  getBranchWithCondition(state, getters) {
    return id =>
      Object.values(state.byId).find(branch =>
        getters.getConditions(branch.id).includes(id)
      ) || null;
  },

  /**
   * Evaluates all the branch conditions and returns the one that is true
   * @param   {String}  id    The branch id
   * @param   {import('@arcweave/arcscript').Interpreter}  interpreter An interpreter instance
   * @returns {String|null}   The truthy condition id or null if there is none
   */
  getTruthyCondition(state, getters, rootState, rootGetters) {
    return (id, interpreter, changes) => {
      const { ifCondition, elseCondition, elseIfConditions } = state.byId[
        id
      ].conditions;
      // Check if the if condition is satisfied
      if (ifCondition) {
        const node = rootGetters['project/data/conditions/getNode'](
          ifCondition
        );

        let script = removeEnclosingParagraph(node.script);
        console.log('BRANCHES checking if condition script for truth:', script);
        script = `<pre><code>${script}</code></pre>`;
        try {
          const { result } = interpreter.runScript(script, changes);
          console.log('BRANCHES script result:', result);
          if (result.condition) return node;
        } catch (error) {
          console.log('CAUGHT PARSE ERROR');
          if (error instanceof ParseError) {
            // do nothing yet
          }
        }
      }
      // Check if any of the else if conditions is satisfied
      if (elseIfConditions) {
        console.log('elseIfConditions exist');
        const found = elseIfConditions.find(cid => {
          const node = rootGetters['project/data/conditions/getNode'](cid);
          let script = removeEnclosingParagraph(node.script);
          script = `<pre><code>${script}</code></pre>`;
          try {
            // Return true or false to find()
            return interpreter.runScript(script).result.condition;
          } catch (error) {
            console.log('CAUGHT PARSE ERROR');
            if (error instanceof ParseError) {
              // do nothing yet
            }
          }
          return false;
        });
        if (found) return rootGetters['project/data/conditions/getNode'](found);
      }
      // If there is an else condition it should be satisfied
      if (elseCondition) {
        const node = rootGetters['project/data/conditions/getNode'](
          elseCondition
        );
        // Always true if the above are failing
        return node;
      }
      // If there is no else and no condition is satisfied there is no truthy condition
      return null;
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'branches',
    container: { type: 'boards', prop: 'branches' },
  }),

  /**
   * Create a new default branch
   * @param {Object}  boardId   The board into which that will be created
   * @param {Object}  position  The position onto the board
   * @param {Object}  props     Customization to the default values
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  async customCreate(context, { boardId, position, props = {}, apply }) {
    const id = uuidv4();
    let x = position.x - 300 / 2;
    let y = position.y - 41 / 2;

    // Constrain the branch position to never leave the board limits
    x = Math.max(x, 0);
    y = Math.max(y, 0);
    x = Math.min(x, 20000 - 300);
    y = Math.min(y, 20000 - 41);

    const { snap, size } = context.rootGetters['project/config/getGrid'];
    if (snap) {
      x = roundToNearestMultiple(x, size);
      y = roundToNearestMultiple(y, size);
    }

    // Apply also custom default styles
    const defaultStyle =
      context.rootGetters['project/config/branches/getDefaultStyle'];

    const branch = { id, ...NEW_BRANCH, ...defaultStyle, x, y, ...props };
    // Create the branch
    await context.dispatch('create', {
      node: branch,
      tId: boardId,
      tIndex: '-',
      apply: false,
    });

    // Create an if condition in this branch
    await context.dispatch('addIfCondition', { id, apply: false });

    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the branch's properties (branch id should be filled)
   * @param {String}  id    The branch id
   * @param {Object}  props Contains only the properties that are changed
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Remove a branch with specified id from the store
   * @param {String}  id      The branch id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Delete the branch conditions
    context.dispatch('deleteConditions', { id, apply: false });
    // Delete the branch
    context.dispatch('delete', { id, apply: false });
    // Deselect it
    context.dispatch(
      'project/config/branches/deselect',
      { id, apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Delete all the conditions of a given branch
   * @param {String}  id      The branch id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteConditions(context, { id, apply }) {
    const ids = context.getters.getConditions(id);
    // Delete the condition objects
    ids.forEach(cid => {
      context.dispatch(
        'project/data/conditions/customDelete',
        {
          id: cid,
          apply: false,
        },
        { root: true }
      );
    });
    updater.complete({ apply });
  },

  /**
   * Create an if condition in the given branch
   * @param {String}  id      The branch id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  async addIfCondition(context, { id, apply }) {
    // Create a condition
    const cid = await context.dispatch(
      'project/data/conditions/customCreate',
      { props: {}, apply: false },
      { root: true }
    );
    // The if condition should always be the first condition in a branch
    const patch = [
      {
        op: 'add',
        path: `/data/branches/byId/${id}/conditions/ifCondition`,
        value: cid,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Create an else condition in the given branch
   * @param {String}  id     The branch id
   * @param {Boolean} apply  Whether to apply the patch after finishing
   */
  async addElseCondition(context, { id, apply }) {
    // Create a condition
    const cid = await context.dispatch(
      'project/data/conditions/customCreate',
      { props: {}, apply: false },
      { root: true }
    );
    // Assign the condition as else condition on the branch
    const patch = [
      {
        op: 'add',
        path: `/data/branches/byId/${id}/conditions/elseCondition`,
        value: cid,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove the else conditional statement from the given branch
   * @param {String}  id    The branch id
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  async deleteElseCondition(context, { id, apply }) {
    const { elseCondition } = context.state.byId[id].conditions;
    if (elseCondition === undefined) return;
    // Remove the condition
    await context.dispatch(
      'project/data/conditions/customDelete',
      { id: elseCondition, apply: false },
      { root: true }
    );
    // Remove the else property from the branch
    const patch = [
      {
        op: 'remove',
        path: `/data/branches/byId/${id}/conditions/elseCondition`,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Create an else-if conditional statement in the given branch
   * @param {String}  id      The branch id
   * @param {String}  index   The index to add the elseIf condition ('-' for end)
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  async addElseIfCondition(context, { id, index, apply }) {
    // Create a condition
    const cid = await context.dispatch(
      'project/data/conditions/customCreate',
      { props: {}, apply: false },
      { root: true }
    );
    const patch = [];
    const { elseIfConditions } = context.state.byId[id].conditions;
    if (elseIfConditions === undefined) {
      patch.push({
        op: 'add',
        path: `/data/branches/byId/${id}/conditions/elseIfConditions`,
        value: [cid],
      });
    } else {
      // Assign the condition as else if condition on the branch
      patch.push({
        op: 'add',
        path: `/data/branches/byId/${id}/conditions/elseIfConditions/${index}`,
        value: cid,
      });
    }
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove an elseIf condition from the given branch
   * @param {String}  id    The branch id
   * @param {String}  cid   The condition id to delete
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  async deleteElseIfCondition(context, { id, cid, apply }) {
    // If no elseif conditions do nothing
    const { elseIfConditions } = context.state.byId[id].conditions;
    if (elseIfConditions === undefined) return;
    // Find the condition
    const index = elseIfConditions.indexOf(cid);
    if (index === -1) return;
    // Remove the condition
    await context.dispatch(
      'project/data/conditions/customDelete',
      { id: cid, apply: false },
      { root: true }
    );
    // If it is the only elseIfCondition remove the property altogether
    if (elseIfConditions.length === 1) {
      await context.dispatch(
        'project/data/branches/deleteProperty',
        {
          id,
          prop: 'conditions/elseIfConditions',
          apply: false,
        },
        { root: true }
      );
    } else {
      // Remove the reference from the elseIf array
      const patch = [
        {
          op: 'remove',
          path: `/data/branches/byId/${id}/conditions/elseIfConditions/${index}`,
        },
      ];
      updater.add(patch);
    }
    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
